/**
 * Non-modal Subject Ask dock. Not the existing modal Sheet (`showModal` + trap).
 */
import { useEffect, type Ref } from "react";

import { useFeatureFlags } from "../../api/feature-flags.js";

import { askInsightsUrl, type AskInsightsSource } from "./AskInsightsEntry.js";
import { AskInsightsSession } from "./AskInsightsSession.js";

export function SubjectAskTrigger({
  source,
  open,
  onOpen,
  triggerRef,
}: {
  source: AskInsightsSource;
  open: boolean;
  onOpen: () => void;
  triggerRef: Ref<HTMLButtonElement>;
}) {
  const flags = useFeatureFlags();
  const on = flags.data?.flags.find((f) => f.key === "insights_ai")?.enabled === true;
  if (!on) return null;

  const title = source.title ? `Ask about ${source.title}` : "Ask Insights";
  const fabClass =
    "inline-flex size-14 items-center justify-center rounded-full bg-(--color-rule-accent) text-sm font-semibold text-fg shadow-modal";

  return (
    <div className="fixed bottom-4 right-4 z-40 md:bottom-10 md:right-10">
      <a href={askInsightsUrl(source)} title={title} className={`${fabClass} md:hidden`}>
        Ask
      </a>
      <button
        ref={triggerRef}
        type="button"
        className={`hidden md:inline-flex ${fabClass}`}
        title={title}
        aria-expanded={open}
        aria-controls="subject-ask-panel"
        onClick={onOpen}
      >
        Ask
      </button>
    </div>
  );
}

export function SubjectAskPanel({
  source,
  open,
  mounted,
  onClose,
  emptyHint,
}: {
  source: AskInsightsSource;
  open: boolean;
  mounted: boolean;
  onClose: () => void;
  emptyHint?: string;
}) {
  const heading = source.title ? `Ask about ${source.title}` : "Ask Insights";

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!mounted) return null;

  return (
    <aside
      id="subject-ask-panel"
      hidden={!open}
      className={
        open
          ? "fixed inset-y-0 right-0 z-(--z-overlay) flex w-[min(28rem,100%)] max-w-[28rem] flex-col border-l border-border bg-surface shadow-modal"
          : "hidden"
      }
      aria-label={heading}
    >
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border px-3 py-2">
        <h2 className="min-w-0 truncate text-sm font-semibold text-fg">{heading}</h2>
        <button
          type="button"
          className="rounded-soft-md px-2 py-1 text-sm text-fg-muted hover:bg-surface-muted hover:text-fg"
          onClick={onClose}
        >
          Close
        </button>
      </div>
      <AskInsightsSession
        key={source.id}
        layout="subject-panel"
        initialSources={[source]}
        lockedPrimary={source}
        hideImplicitRag
        emptyHint={emptyHint ?? "Ask about costs, evidence or current decisions."}
        autoFocusComposer={open}
      />
    </aside>
  );
}
